import React, { useContext, useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApplicationContext } from '../../context/GlobalState';
import { extractLocaleFromPath, localizedPath, t } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildBlogPostSchema } from '../../utils/schemaMarkup';
import { getLocalizedArticle } from './BlogIndex';
import gaHero from '../../assets/images/heroes/ga-hero-main.webp';
import ncHero from '../../assets/images/heroes/nc-hero-main.webp';

const BRAND_HEROES = { gso: gaHero, 'apex-nc': ncHero, 'apex-tn': ncHero };

function formatDate(dateStr, locale = 'en') {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const localeMap = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ko: 'ko-KR', ja: 'ja-JP', fr: 'fr-FR' };
  return d.toLocaleDateString(localeMap[locale] || 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Renders article body which may be markdown-like or raw HTML from WP REST API.
 * We use dangerouslySetInnerHTML for HTML content, falling back to text parsing.
 */
function ArticleBody({ body }) {
  if (!body) return null;

  // Detect HTML content (has HTML tags)
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  if (isHtml) {
    return (
      <div
        className="article-body"
        style={{ lineHeight: 1.8, fontSize: '1.05rem', color: 'var(--color-text)' }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
      />
    );
  }

  // Markdown-like parsing
  return (
    <div style={{ lineHeight: 1.8, fontSize: '1.05rem', color: 'var(--color-text)' }}>
      {body.split('\n\n').map((block, i) => {
        if (block.startsWith('## ')) {
          return <h2 key={i} style={{ fontSize: '1.5rem', margin: '32px 0 16px', color: 'var(--color-primary)' }}>{block.replace(/^## /, '')}</h2>;
        }
        if (block.startsWith('### ')) {
          return <h3 key={i} style={{ fontSize: '1.25rem', margin: '24px 0 12px', color: 'var(--color-primary)' }}>{block.replace(/^### /, '')}</h3>;
        }
        if (block.startsWith('# ')) {
          return <h1 key={i} style={{ fontSize: '1.75rem', margin: '36px 0 18px', color: 'var(--color-primary)' }}>{block.replace(/^# /, '')}</h1>;
        }
        if (block.startsWith('*\n')) {
          return <hr key={i} style={{ margin: '32px 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />;
        }
        // Handle bold inline
        const formatted = block.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <p key={i} style={{ marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted) }} />;
      })}
    </div>
  );
}

// Cache to avoid re-fetching across navigations within same session
let articlesCache = null;

export default function BlogPost() {
  const { state } = useContext(ApplicationContext);
  const { postSlug } = useParams();
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const heroImg = BRAND_HEROES[brandKey] || gaHero;

  const [articles, setArticles] = useState(articlesCache || []);
  const [loading, setLoading] = useState(!articlesCache);

  useEffect(() => {
    if (articlesCache) return;
    fetch('/data/articles-scraped.json')
      .then(res => res.json())
      .then(data => {
        articlesCache = data;
        setArticles(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load articles:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="container section-padding" style={{ textAlign: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>{t("common.loading", locale)}</p>
      </div>
    );
  }

  // Brand-scope the article lookup to prevent cross-site content bleed
  const BRAND_SOURCE = { gso: 'gaspineortho', 'apex-nc': 'apexosn.com', 'apex-tn': 'apexosntn' };
  const sourceMatch = BRAND_SOURCE[brandKey] || '';
  const brandArticles = sourceMatch
    ? articles.filter(a => a.source && a.source.includes(sourceMatch))
    : articles;
  const article = brandArticles.find((a) => a.slug === postSlug);

  if (!article) {
    return (
      <div className="container section-padding" style={{ textAlign: 'center' }}>
        <h1>{t('blog.notFound', locale)}</h1>
        <p>{t('blog.notFoundDesc', locale)}</p>
        <Link to={localizedPath('/blog', locale)} style={{ color: 'var(--color-accent)', fontWeight: 600 }}>{t("blog.backToBlog", locale)}</Link>
      </div>
    );
  }

  const localized = getLocalizedArticle(article, locale);

  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{localized.title} | {brand.name}</title>
        <meta name="description" content={localized.excerpt} />
        {article.url && <link rel="canonical" href={article.url} />}
      </Helmet>

      <HreflangTags baseDomain={brand?.domain || 'gaspineortho.com'} pathWithoutLocale={`/blog/${postSlug}`} />

      {/* BlogPosting JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBlogPostSchema({ post: article, brand })) }}
      />

      {/* Hero */}
      <section
        style={{
          backgroundImage: article.images && article.images[0]
            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${article.images[0]})`
            : `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          padding: '100px 0 60px',
          textAlign: 'center',
        }}
      >
        <div className="container">
          <h1 style={{ fontSize: '2.25rem', marginBottom: 12, color: 'white' }}>{localized.title}</h1>
          <p style={{ opacity: 0.8 }}>{formatDate(article.date, locale)} • {brand.name}</p>
        </div>
      </section>

      {/* Article body */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: 800, margin: '0 auto' }}>
          <ArticleBody body={localized.body} />

          {/* Related images */}
          {article.images && article.images.length > 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 32 }}>
              {article.images.slice(1).map((img, i) => (
                <img key={i} src={img} alt="" style={{ width: '100%', borderRadius: 8, objectFit: 'cover', height: 160 }} loading="lazy" />
              ))}
            </div>
          )}

          <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--color-border)' }}>
            <Link to={localizedPath('/blog', locale)} style={{ color: 'var(--color-accent)', fontWeight: 600, fontSize: '1rem' }}>{t("blog.backToBlog", locale)}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
